/* ============================================================================
 * IMMERSION_WIZARD.EXE — build an Immersion, in nine steps
 * ----------------------------------------------------------------------------
 * WHAT THIS DESK IS FOR
 * An Immersion is not a skin. A skin is data. An Immersion is a CONTAINER: one
 * NFT whose token-bound account holds identity, roles, modules and assets for
 * one person or project. You cannot forge one in a single panel, because the
 * parts are ordered — the container is derived from a token, the modules decide
 * which roles are legal, and the palette is applied at runtime on top.
 *
 * THE RULE THAT SHAPES EVERY FIELD HERE
 * A wizard may not invent a value. A missing input becomes the word `unknown` —
 * never a plausible-looking address, never a zero standing in for a failed read.
 * That is why the derivation opens EMPTY: no default token is seeded, and a
 * missing input stays the word `unknown` rather than a plausible address.
 *
 * WHAT IT OWES, AND WHAT IT DOES NOT HAVE
 *   • the container   ERC-6551, derived LOCALLY (src/lib/immersion.ts). Nothing
 *                     is created, nothing is minted, no RPC is called.
 *   • the charter     src/lib/charter.ts — the seven rights and their real
 *                     status. Review shows them as they stand, not as hoped.
 *   • the roster      the 15 designations → one uint256. PROPOSED, NOT ENFORCED:
 *                     there is no roles contract anywhere in the org's repos.
 *   • the skin        SKIN_BUILDER's model (src/lib/skinforge.ts) — step 05 is
 *                     the forge, which is why the two desks point at each other.
 *
 * NOTHING IS SIGNED, MINTED OR SENT. The last step records an intent on this
 * device and says so; the reserve button does not touch a chain.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { appById } from './registry';
import {
  ERC6551_ACCOUNT_IMPLEMENTATION,
  ERC6551_REGISTRY,
  IMMERSION_CHAIN_ID,
  PILLARS,
  computeTokenBoundAccount,
  type PillarId,
} from '../lib/immersion';
import { PERMISSION, PERMISSION_KEYS, ROLES, permissionsFor } from '../lib/roles';
import {
  STRUCTURAL_OPTIONS,
  TOKEN_GROUPS,
  applyForgedSkin,
  blankSkin,
  diffFromMaster,
  skinToCss,
  skinToJson,
  slug,
  type ForgedSkin,
  type StructuralKnobs,
} from '../lib/skinforge';
import { buildCharter, charterSummary } from '../lib/charter';
import { base, baseSepolia } from '../lib/supercompute';

/* ── the nine steps, and what each is gated on ────────────────────────────── */

type StepId =
  | 'identity'
  | 'container'
  | 'pillars'
  | 'roster'
  | 'dao'
  | 'skin'
  | 'surface'
  | 'review'
  | 'issue';

interface StepDef {
  id: StepId;
  label: string;
  /** The thing that must exist before this step means anything. */
  gate: string;
}

const STEPS: readonly StepDef[] = [
  { id: 'identity', label: 'Identity', gate: 'a name — ENS is the precondition' },
  { id: 'container', label: 'Container', gate: 'a token contract and an id' },
  { id: 'pillars', label: 'Pillars', gate: 'a container to hang modules on' },
  { id: 'roster', label: 'Roster', gate: 'modules decide which roles are legal' },
  { id: 'dao', label: 'Governance', gate: 'a roster — a vote is taken by the members' },
  { id: 'skin', label: 'Skin', gate: 'the palette applied over the container' },
  { id: 'surface', label: 'Surface', gate: 'the name and the palette it is painted with' },
  { id: 'review', label: 'Review', gate: 'every answer on one surface' },
  { id: 'issue', label: 'Issue', gate: 'nothing is minted here — the payload is' },
];

const STEP_COPY: Record<StepId, string> = {
  identity:
    'The charter reads its holder from ENS and nothing else, so a name is the first thing that must exist.',
  container:
    'The token-bound account is computed from the registry, the implementation, the salt, the chain and the token. It is arithmetic, so it is checkable — and it is not a lookup, because nothing is deployed.',
  pillars:
    'Four pillars, twelve modules. A pillar with no module behind it still takes its slot and says which module is missing.',
  roster:
    'Fifteen designations compress to one uint256. The mask is a local predicate — no contract enforces it.',
  dao:
    'A DAO is deployed, not bound: it is created by a transaction, and the treasury is the container. A passed vote and the account that pays are one address.',
  skin:
    'A palette and three structural switches, exactly what SKIN_BUILDER.EXE may change. Structure stays with the master.',
  surface:
    'The mark in the chrome and the first screen: one headline, one tagline, one notice while it is true. A page that opens on nothing is a page nobody returns to.',
  review:
    'The charter as it actually stands, the roster as it computes, the skin as it diffs.',
  issue:
    'CSS, JSON and a manifest. The CSS and the JSON are real; the reservation is an intent recorded on this device.',
};

/* ── the draft, and how it survives a closed window ───────────────────────── */

type DaoModel = 'token' | 'holder' | 'conviction';

/** Governance is a deployed DAO — the treasury is the container it governs. */
interface DaoSettings {
  name: string;
  chainId: number;
  token: string;
  symbol: string;
  model: DaoModel;
  quorum: string;
  period: string;
}

/** The chrome mark and the first screen: what the Immersion says the moment it opens. */
interface SurfaceSettings {
  logoMode: 'mark' | 'custom' | 'none';
  logoName: string;
  logoData: string;
  headline: string;
  tagline: string;
  notice: string;
  noticeKind: 'info' | 'ok' | 'warn' | 'err';
  wallpaper: 'dither' | 'grid' | 'flat';
}

interface Draft {
  label: string;
  id: string;
  handle: string;
  provenance: 'issued' | 'brought';
  chainId: number;
  tokenContract: string;
  tokenId: string;
  salt: string;
  pillars: PillarId[];
  roles: string[];
  skin: ForgedSkin;
  dao: DaoSettings;
  surface: SurfaceSettings;
}

const DRAFT_KEY = 'supercompute-webos:immersion-draft:v1';
const RESERVE_KEY = 'supercompute-webos:immersion-reservations:v1';

const ZERO_SALT_TEXT = `0x${'00'.repeat(32)}`;

function blankDraft(): Draft {
  const skin = blankSkin('Immersion');
  return {
    label: 'Immersion',
    id: skin.id,
    handle: '',
    provenance: 'issued',
    chainId: IMMERSION_CHAIN_ID,
    tokenContract: '',
    tokenId: '',
    salt: ZERO_SALT_TEXT,
    pillars: ['identity', 'operations', 'modules', 'finance'],
    roles: ['founder', 'franchisee'],
    skin,
    dao: {
      name: '',
      chainId: base.id,
      token: '',
      symbol: '',
      model: 'token',
      quorum: '51',
      period: '5d',
    },
    surface: {
      logoMode: 'mark',
      logoName: '',
      logoData: '',
      headline: 'Welcome to the Immersion',
      tagline: 'Four pillars, one container.',
      notice: '',
      noticeKind: 'info',
      wallpaper: 'dither',
    },
  };
}

function loadState(): { step: number; draft: Draft } {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return { step: 0, draft: blankDraft() };
    const parsed = JSON.parse(raw) as { step?: number; draft?: Partial<Draft> };
    if (!parsed.draft) return { step: 0, draft: blankDraft() };
    // Merge onto a blank so a draft written by an older build cannot leave a
    // field undefined — a half-loaded draft is how a wizard invents a value.
    const base = blankDraft();
    const draft: Draft = {
      ...base,
      ...parsed.draft,
      skin: { ...base.skin, ...parsed.draft.skin },
      dao: { ...base.dao, ...parsed.draft.dao },
      surface: { ...base.surface, ...parsed.draft.surface },
    };
    const step = Math.min(Math.max(parsed.step ?? 0, 0), STEPS.length - 1);
    return { step, draft };
  } catch {
    return { step: 0, draft: blankDraft() };
  }
}

interface Reservation {
  at: string;
  name: string;
  id: string;
  chainId: number;
  account: string | null;
  roles: string[];
  pillars: string[];
  skin: string;
  governance?: { chainId: number; model: string };
  surface?: { logoMode: string; headline: string };
}

function loadReservations(): Reservation[] {
  try {
    const raw = localStorage.getItem(RESERVE_KEY);
    return raw ? (JSON.parse(raw) as Reservation[]) : [];
  } catch {
    return [];
  }
}

/* ── input predicates — the only place a value is allowed to be "unknown" ─── */

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const SALT_RE = /^0x[0-9a-fA-F]{64}$/;

function isAddress(v: string): v is `0x${string}` {
  return ADDRESS_RE.test(v.trim());
}
function isSalt(v: string): v is `0x${string}` {
  return SALT_RE.test(v.trim());
}
function isUint(v: string): boolean {
  return /^\d+$/.test(v.trim());
}

const CHAIN_OPTIONS = [
  { id: base.id, label: 'Base (8453)' },
  { id: baseSepolia.id, label: 'Base Sepolia (84532)' },
] as const;

function shorten(hex: string): string {
  return hex.length <= 18 ? hex : `${hex.slice(0, 10)}…${hex.slice(-6)}`;
}

/* ── the desk ─────────────────────────────────────────────────────────────── */

export function ImmersionWizard() {
  const [{ step, draft }, setState] = useState(loadState);
  const [msg, setMsg] = useState<string | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>(loadReservations);

  const setDraft = useCallback((fn: (d: Draft) => Draft) => {
    setState((s) => ({ ...s, draft: fn(s.draft) }));
  }, []);
  const setStep = useCallback((n: number) => {
    setState((s) => ({ ...s, step: Math.min(Math.max(n, 0), STEPS.length - 1) }));
  }, []);

  const current = STEPS[step];

  // The draft survives a closed window — a wizard that forgets step 05 is worse
  // than no wizard.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, draft }));
    } catch {
      /* storage refused — the desk still works, it just forgets */
    }
  }, [step, draft]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === 'ArrowRight') setStep(step + 1);
      if (e.key === 'ArrowLeft') setStep(step - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, setStep]);

  /* ── the derived container ───────────────────────────────────────────────── */

  const account = useMemo<`0x${string}` | null>(() => {
    if (!isAddress(draft.tokenContract) || !isSalt(draft.salt) || !isUint(draft.tokenId)) {
      return null;
    }
    try {
      return computeTokenBoundAccount(
        ERC6551_ACCOUNT_IMPLEMENTATION,
        draft.salt.trim() as `0x${string}`,
        BigInt(draft.chainId),
        draft.tokenContract.trim() as `0x${string}`,
        BigInt(draft.tokenId.trim()),
      );
    } catch {
      return null;
    }
  }, [draft.tokenContract, draft.salt, draft.tokenId, draft.chainId]);

  const charter = useMemo(
    () =>
      buildCharter({
        holderEns: draft.handle.trim() ? draft.handle.trim() : null,
        tokenContract: draft.tokenContract,
        tokenId: isUint(draft.tokenId) ? BigInt(draft.tokenId.trim()) : undefined,
        chainId: draft.chainId,
      }),
    [draft.handle, draft.tokenContract, draft.tokenId, draft.chainId],
  );

  /* ── the roster ──────────────────────────────────────────────────────────── */

  const mask = useMemo(() => permissionsFor(draft.roles), [draft.roles]);
  const maskHex = `0x${mask.toString(16).padStart(64, '0')}`;
  const bitsSet = PERMISSION_KEYS.filter((k) => (mask & PERMISSION[k]) !== 0n).length;
  const charterBits = [
    { key: 'LEXICON_STEWARD' as const, label: 'Steward a lexicon' },
    { key: 'VALUES_SCORE' as const, label: 'Sign the period score' },
    { key: 'CHARTER_CREATE' as const, label: 'Create instruments' },
  ];
  const charterBitsHeld = charterBits.filter((b) => (mask & PERMISSION[b.key]) !== 0n).length;

  /* ── the skin ────────────────────────────────────────────────────────────── */

  const skinDiff = useMemo(() => diffFromMaster(draft.skin), [draft.skin]);
  const skinCss = useMemo(() => skinToCss(draft.skin), [draft.skin]);

  const setToken = useCallback(
    (name: string, value: string, master: string) => {
      setDraft((d) => {
        const palette = { ...d.skin.palette };
        if (!value || value === master) delete palette[name];
        else palette[name] = value;
        return { ...d, skin: { ...d.skin, palette } };
      });
    },
    [setDraft],
  );

  const setStructural = useCallback(
    (key: keyof StructuralKnobs, value: string) => {
      setDraft((d) => ({
        ...d,
        skin: {
          ...d.skin,
          structural: {
            ...d.skin.structural,
            [key]: value as StructuralKnobs[typeof key],
          },
        },
      }));
    },
    [setDraft],
  );

  /* ── applying the skin: the DESKTOP is the preview ─────────────────────────
   * A palette cannot be previewed by writing the raw tokens on a subtree. The
   * semantic aliases (`--ui-accent: var(--gold)`) are DECLARED at :root, and a
   * custom property is substituted where it is declared — so an override of
   * `--gold` inside a div reaches nothing: `--ui-accent` already computed at
   * :root and inherits its finished value down.
   *
   * There are two ways out and only one of them is right. Scoping a copy of the
   * alias chain into the preview would create a SECOND declaration site for the
   * palette — the exact drift the token layer exists to prevent, and a preview
   * that can disagree with what applies. So the preview is the real thing:
   * the skin is written onto the document root by the same helper
   * SKIN_BUILDER.EXE uses, and reverts when it is switched off or the desk
   * closes. What you see is what applies, because it is the applying. */
  const [skinApplied, setSkinApplied] = useState(false);

  useEffect(() => () => applyForgedSkin(null), []);

  // Both directions, or switching the skin off leaves the tokens on the root and
  // the OS keeps a palette the operator has already declined.
  useEffect(() => {
    applyForgedSkin(skinApplied ? draft.skin : null);
  }, [skinApplied, draft.skin]);

  /* ── the payload (step 07) ───────────────────────────────────────────────── */

  const manifest = useMemo(
    () =>
      JSON.stringify(
        {
          version: 1,
          kind: 'immersion-draft',
          generatedAt: new Date().toISOString(),
          identity: {
            label: draft.label,
            id: draft.id,
            ens: draft.handle.trim() || null,
            nameProvenance: draft.provenance,
          },
          container: {
            standard: 'ERC-6551',
            registry: ERC6551_REGISTRY,
            implementation: ERC6551_ACCOUNT_IMPLEMENTATION,
            chainId: draft.chainId,
            tokenContract: isAddress(draft.tokenContract) ? draft.tokenContract.trim() : null,
            tokenId: isUint(draft.tokenId) ? draft.tokenId.trim() : null,
            salt: isSalt(draft.salt) ? draft.salt.trim() : null,
            account,
            derived: account !== null,
            note: account
              ? 'Derived locally. Nothing is deployed and nothing is minted.'
              : 'Not derived — a token contract, a token id and a 32-byte salt are required.',
          },
          pillars: draft.pillars,
          roster: {
            roles: draft.roles,
            mask: maskHex,
            bitsSet,
            enforcement: 'proposed — not enforced',
          },
          skin: draft.skin,
          governance: {
            name: draft.dao.name.trim() || null,
            chainId: draft.dao.chainId,
            token: isAddress(draft.dao.token) ? draft.dao.token.trim() : null,
            symbol: draft.dao.symbol.trim() || null,
            model: draft.dao.model,
            quorum: Number(draft.dao.quorum) || null,
            period: draft.dao.period.trim() || null,
            treasury: account,
            note: 'A DAO is deployed, not bound — created by a transaction, governing the derived container.',
          },
          surface: {
            logoMode: draft.surface.logoMode,
            // The data URI is deliberately NOT written here: the manifest is a
            // portable document, and a base64 mark would make it unreadable.
            logoRef: draft.surface.logoMode === 'custom' ? draft.surface.logoName || null : 'the brand mark',
            headline: draft.surface.headline.trim() || null,
            tagline: draft.surface.tagline.trim() || null,
            notice: draft.surface.notice.trim() || null,
            noticeKind: draft.surface.noticeKind,
            wallpaper: draft.surface.wallpaper,
          },
          charter: {
            state: charter.state,
            rights: charter.rights.map((r) => ({ key: r.key, status: r.status })),
          },
        },
        null,
        2,
      ),
    [draft, account, maskHex, bitsSet, charter],
  );

  const copy = useCallback((text: string, what: string) => {
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setMsg(`${what} copied.`))
      .catch(() => setMsg('Clipboard blocked — select the text and copy it.'));
  }, []);

  const reserve = useCallback(() => {
    const record: Reservation = {
      at: new Date().toISOString(),
      name: draft.label,
      id: draft.id,
      chainId: draft.chainId,
      account,
      roles: draft.roles,
      pillars: draft.pillars,
      skin: draft.skin.id,
      governance: {
        chainId: draft.dao.chainId,
        model: draft.dao.model,
      },
      surface: {
        logoMode: draft.surface.logoMode,
        headline: draft.surface.headline.trim(),
      },
    };
    setReservations((prev) => {
      const next = [record, ...prev].slice(0, 50);
      try {
        localStorage.setItem(RESERVE_KEY, JSON.stringify(next));
      } catch {
        /* non-fatal */
      }
      return next;
    });
    setMsg('Intent recorded on this device. Nothing was sent.');
  }, [draft, account]);

  /* ── render ──────────────────────────────────────────────────────────────── */

  return (
    <div className="dsp page">
      <header className="page__head">
        <img
          className="page__crest"
          src={appById('wizard')?.tile}
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
        <div className="page__titles">
          <p className="eyebrow">immersion wizard · container forge</p>
          <h1 className="h1">Nine steps to a container</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-admin">
            step {step + 1} / {STEPS.length}
          </span>
          <span
            className={account ? 'badge badge-live' : 'badge badge-claude'}
            title={account ? 'a token-bound account is derived' : 'no token-bound account yet'}
          >
            {account ? 'derived' : 'not derived'}
          </span>
        </div>
      </header>

      <div className="tabs page__tabs wzsteps" role="tablist" aria-label="Wizard steps">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            role="tab"
            type="button"
            aria-selected={s.id === current.id}
            tabIndex={s.id === current.id ? 0 : -1}
            onClick={() => setStep(i)}
          >
            {String(i + 1).padStart(2, '0')} {s.label}
          </button>
        ))}
      </div>

      <div className="page__bar">
        <div className="grow">
          <p className="section-h">
            {current.label} <span className="dim">gated on {current.gate}</span>
          </p>
          <p className="cap">{STEP_COPY[current.id]}</p>
        </div>
        <div className="hstack">
          <button
            type="button"
            className="btn"
            disabled={step === 0}
            onClick={() => setStep(step - 1)}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={step === STEPS.length - 1}
            onClick={() => setStep(step + 1)}
          >
            Next step
          </button>
        </div>
      </div>

      <div className="wz-split">
        <div className="page__body">
          {current.id === 'identity' && (
            <div className="sheet">
              <p className="section-h">
                identity <span className="dim">whose immersion this is</span>
              </p>
              <div className="grid2">
                <div>
                  <label className="lbl micro" htmlFor="wz-label">
                    Label
                  </label>
                  <input
                    id="wz-label"
                    className="field"
                    value={draft.label}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        label: e.target.value,
                        id: slug(e.target.value),
                        skin: { ...d.skin, label: e.target.value, wordmark: e.target.value.toUpperCase() },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-id">
                    id · slug
                  </label>
                  <input
                    id="wz-id"
                    className="field"
                    value={draft.id}
                    onChange={(e) => setDraft((d) => ({ ...d, id: slug(e.target.value) }))}
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-handle">
                    ENS handle
                  </label>
                  <input
                    id="wz-handle"
                    className="field"
                    placeholder="name.eth"
                    spellCheck={false}
                    value={draft.handle}
                    onChange={(e) => setDraft((d) => ({ ...d, handle: e.target.value.trim() }))}
                  />
                </div>
                <div>
                  <span className="lbl micro">Name provenance</span>
                  <div className="hstack">
                    {(['issued', 'brought'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        className="btn"
                        aria-pressed={draft.provenance === p}
                        onClick={() => setDraft((d) => ({ ...d, provenance: p }))}
                      >
                        {p === 'issued' ? 'bought through us' : "the operator's own ENS"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="row">
                <span className="row__k">Charter reads</span>
                <span className="row__v">
                  {charter.holderEns ? (
                    <span className="mono">{charter.holderEns}</span>
                  ) : (
                    <span className="tag tag--warn">unknown — no ENS handle entered</span>
                  )}
                </span>
              </div>
              <div className="row">
                <span className="row__k">Lexicon namespace</span>
                <span className="row__v mono">
                  {charter.lexiconNamespace ?? (
                    <span className="tag tag--muted">not derivable without a handle</span>
                  )}
                </span>
              </div>
              <p className="note">
                A name is either bought through us or already the operator&rsquo;s. This desk
                registers nothing and resolves nothing — ENS is read on mainnet by the
                charter, not by this form.
              </p>
            </div>
          )}

          {current.id === 'container' && (
            <div className="sheet">
              <p className="section-h">
                the container <span className="dim">erc-6551 · computed here</span>
              </p>
              <div className="grid2">
                <div>
                  <label className="lbl micro" htmlFor="wz-chain">
                    Chain
                  </label>
                  <select
                    id="wz-chain"
                    className="field"
                    value={draft.chainId}
                    onChange={(e) => setDraft((d) => ({ ...d, chainId: Number(e.target.value) }))}
                  >
                    {CHAIN_OPTIONS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-tokenid">
                    Token id
                  </label>
                  <input
                    id="wz-tokenid"
                    className="field mono"
                    inputMode="numeric"
                    placeholder="unknown"
                    spellCheck={false}
                    value={draft.tokenId}
                    onChange={(e) => setDraft((d) => ({ ...d, tokenId: e.target.value.trim() }))}
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-contract">
                    Token contract
                  </label>
                  <input
                    id="wz-contract"
                    className="field mono"
                    placeholder="unknown — 0x…"
                    spellCheck={false}
                    value={draft.tokenContract}
                    onChange={(e) => setDraft((d) => ({ ...d, tokenContract: e.target.value.trim() }))}
                  />
                  {draft.tokenContract.trim() !== '' && !isAddress(draft.tokenContract) && (
                    <p className="err">Not an address — 20 bytes, 0x-prefixed.</p>
                  )}
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-salt">
                    Salt · bytes32
                  </label>
                  <input
                    id="wz-salt"
                    className="field mono"
                    spellCheck={false}
                    value={draft.salt}
                    onChange={(e) => setDraft((d) => ({ ...d, salt: e.target.value.trim() }))}
                  />
                  {!isSalt(draft.salt) && (
                    <p className="err">A salt is 32 bytes of hex, not hashed again by create2.</p>
                  )}
                </div>
              </div>

              <div className="hstack">
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    setDraft((d) => ({ ...d, tokenContract: '', tokenId: '', salt: ZERO_SALT_TEXT }))
                  }
                >
                  Clear
                </button>
              </div>

              <div className="term">
                <p className="section-h">
                  derived account <span className="dim">the franchise address</span>
                </p>
                {account ? (
                  <p className="code">{account}</p>
                ) : (
                  <p className="empty">
                    unknown — an account cannot be derived without a token contract, a token id
                    and a 32-byte salt. Nothing is guessed to fill the gap.
                  </p>
                )}
                <dl className="kv" style={{ marginTop: 'var(--s-3)' }}>
                  <dt>Registry</dt>
                  <dd className="mono">{shorten(ERC6551_REGISTRY)}</dd>
                  <dt>Implementation</dt>
                  <dd className="mono">{shorten(ERC6551_ACCOUNT_IMPLEMENTATION)}</dd>
                  <dt>Chain</dt>
                  <dd className="mono">{draft.chainId}</dd>
                  <dt>Salt</dt>
                  <dd className="mono">{isSalt(draft.salt) ? shorten(draft.salt) : 'invalid'}</dd>
                </dl>
              </div>

              <div className="notice notice--warn">
                <span className="tick" aria-hidden="true" />
                <span>
                  <strong>No collection is deployed.</strong> This is a derivation, not a lookup:
                  nothing exists at that address and no toast or chip in the OS claims otherwise.
                  The registry and the implementation above are the deployed canonical pair on
                  Base and Base Sepolia.
                </span>
              </div>
            </div>
          )}

          {current.id === 'pillars' && (
            <div className="sheet wz-wrap">
              <p className="section-h">
                pillars <span className="dim">what this immersion carries</span>
              </p>
              <div className="rows">
                {PILLARS.map((p) => {
                  const on = draft.pillars.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={on ? 'lrow done' : 'lrow'}
                      aria-pressed={on}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          pillars: on
                            ? d.pillars.filter((x) => x !== p.id)
                            : [...d.pillars, p.id],
                        }))
                      }
                    >
                      <span className="toggle" aria-hidden="true" />
                      <span className="grow">
                        <span className="lrow__t">{p.label}</span>
                        <span className="sub">{p.blurb}</span>
                      </span>
                      <span className="hstack" style={{ justifyContent: 'flex-end' }}>
                        {p.modules.map((m) => {
                          const app = m.appId ? appById(m.appId) : undefined;
                          return (
                            <span
                              key={m.label}
                              className={app ? 'tag tag--muted' : 'tag tag--warn'}
                              title={app ? `${app.title} — shipped` : 'specified, not built'}
                            >
                              {m.label}
                            </span>
                          );
                        })}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="note">
                A module that resolves to a desk in the registry is shipped; the rest are
                specified and say so on their own chip. Turning a pillar off does not delete a
                desk — it declares that this Immersion does not carry it.
              </p>
            </div>
          )}

          {current.id === 'roster' && (
            <div className="sheet wz-wrap">
              <p className="section-h">
                roster <span className="dim">15 designations → one uint256</span>
              </p>
              <div className="rows">
                {ROLES.map((r) => {
                  const on = draft.roles.includes(r.id);
                  const count = PERMISSION_KEYS.filter(
                    (k) => (r.permissions & PERMISSION[k]) !== 0n,
                  ).length;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={on ? 'lrow done' : 'lrow'}
                      aria-pressed={on}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          roles: on ? d.roles.filter((x) => x !== r.id) : [...d.roles, r.id],
                        }))
                      }
                    >
                      <span className="toggle" aria-hidden="true" />
                      <span className="grow">
                        <span className="lrow__t">
                          {r.label} <span className="row__k" style={{ flex: 'none' }}>{r.group}</span>
                        </span>
                        <span className="sub">{r.purpose}</span>
                      </span>
                      <span className="mono">{count}/30</span>
                    </button>
                  );
                })}
              </div>
              <div className="row">
                <span className="row__k">Mask</span>
                <span className="row__v">
                  <code className="code" style={{ display: 'block' }}>
                    {maskHex}
                  </code>
                </span>
              </div>
              <dl className="kv">
                <dt>Roles held</dt>
                <dd className="mono">
                  {draft.roles.length} of {ROLES.length}
                </dd>
                <dt>Capabilities granted</dt>
                <dd className="mono">
                  {bitsSet} of {PERMISSION_KEYS.length}
                </dd>
                <dt>Charter bits</dt>
                <dd className="mono">
                  {charterBitsHeld} of {charterBits.length}
                </dd>
              </dl>
              <div className="notice notice--brass">
                <span className="tick" aria-hidden="true" />
                <span>
                  <strong>Proposed · not enforced.</strong> There is no roles contract in any
                  Supercompute repo, so this mask is a local predicate, not an access check.
                  Nothing on-chain consults it.
                </span>
              </div>
            </div>
          )}

          {current.id === 'dao' && (
            <div className="sheet">
              <p className="section-h">
                governance <span className="dim">a deployed DAO over the derived treasury</span>
              </p>
              <p className="cap">
                The roster says who is here. Governance says how they act together — the forum
                where a proposal is argued, the space where it is voted, the threshold that makes
                it pass, and the account that pays for it. A DAO is deployed by a transaction, so
                this step records the DAO and refuses to invent one it cannot name.
              </p>
              <div className="grid2">
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-name">
                    DAO name
                  </label>
                  <input
                    id="wz-dao-name"
                    className="field"
                    placeholder="Supercompute Immersion DAO"
                    value={draft.dao.name}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, dao: { ...d.dao, name: e.target.value } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-chain">
                    Network
                  </label>
                  <select
                    id="wz-dao-chain"
                    className="field"
                    value={draft.dao.chainId}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        dao: { ...d.dao, chainId: Number(e.target.value) },
                      }))
                    }
                  >
                    {CHAIN_OPTIONS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-token">
                    Governance token
                  </label>
                  <input
                    id="wz-dao-token"
                    className="field mono"
                    placeholder="0x… ERC-20"
                    spellCheck={false}
                    value={draft.dao.token}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, dao: { ...d.dao, token: e.target.value.trim() } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-symbol">
                    Symbol
                  </label>
                  <input
                    id="wz-dao-symbol"
                    className="field"
                    value={draft.dao.symbol}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, dao: { ...d.dao, symbol: e.target.value } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-model">
                    Voting model
                  </label>
                  <select
                    id="wz-dao-model"
                    className="field"
                    value={draft.dao.model}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        dao: { ...d.dao, model: e.target.value as DaoModel },
                      }))
                    }
                  >
                    <option value="token">Token-weighted</option>
                    <option value="holder">One holder, one vote</option>
                    <option value="conviction">Conviction — weight accrues over time</option>
                  </select>
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-quorum">
                    Quorum %
                  </label>
                  <input
                    id="wz-dao-quorum"
                    className="field"
                    inputMode="numeric"
                    placeholder="51"
                    value={draft.dao.quorum}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        dao: {
                          ...d.dao,
                          quorum: e.target.value.replace(/[^\d]/g, '').slice(0, 3),
                        },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-dao-period">
                    Voting period
                  </label>
                  <input
                    id="wz-dao-period"
                    className="field"
                    placeholder="5d"
                    spellCheck={false}
                    value={draft.dao.period}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, dao: { ...d.dao, period: e.target.value.trim() } }))
                    }
                  />
                </div>
              </div>
              <div className="row">
                <span className="row__k">Treasury — derived</span>
                <span className="row__v">
                  {account ? (
                    <span className="mono">{account}</span>
                  ) : (
                    <span className="tag tag--warn">unknown — complete step 02</span>
                  )}
                </span>
              </div>
              <div className="row">
                <span className="row__k">Membership</span>
                <span className="row__v">
                  {draft.roles.length ? (
                    <span className="mono">
                      {draft.roles.length} roles · {bitsSet}/30 bits
                    </span>
                  ) : (
                    <span className="tag tag--warn">no roles bound — step 04</span>
                  )}
                </span>
              </div>
              <p className="note">
                The treasury is the container. What the DAO holds, the Immersion holds — one
                address, not two that have to be reconciled. It is derived in step 02 and never
                typed here.
              </p>
            </div>
          )}

          {current.id === 'skin' && (
            <>
              {TOKEN_GROUPS.map((g) => (
                <div className="sheet" key={g.id}>
                  <p className="section-h">
                    palette <span className="dim">{g.label.toLowerCase()}</span>
                  </p>
                  {g.tokens.map((t) => {
                    const overridden = draft.skin.palette[t.name];
                    const hex = /^#[0-9a-f]{6}$/i.test(t.master);
                    return (
                      <div className="row" key={t.name}>
                        <span className="row__k">{t.label}</span>
                        <span className="row__v hstack">
                          {hex ? (
                            <input
                              type="color"
                              aria-label={`${t.label} colour`}
                              value={overridden ?? t.master}
                              onChange={(e) => setToken(t.name, e.target.value, t.master)}
                              style={{
                                width: 30,
                                height: 22,
                                padding: 0,
                                border: '1px solid var(--ui-border)',
                                borderRadius: 'var(--ui-radius-ctl)',
                                background: 'transparent',
                              }}
                            />
                          ) : (
                            <span className="tag tag--muted" title="not a hex value — edit as text">
                              {overridden ?? t.master}
                            </span>
                          )}
                          <span className="mono">{overridden ?? `${t.master} (master)`}</span>
                          {overridden && (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setToken(t.name, t.master, t.master)}
                            >
                              Reset
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <p className="note">{g.note}</p>
                </div>
              ))}

              <div className="sheet">
                <p className="section-h">
                  structure <span className="dim">three switches, no geometry</span>
                </p>
                {STRUCTURAL_OPTIONS.map((k) => (
                  <div key={k.key} style={{ marginBottom: 'var(--s-3)' }}>
                    <span className="lbl micro">{k.label}</span>
                    <div className="hstack">
                      {k.options.map((o) => (
                        <button
                          key={o}
                          type="button"
                          className="btn"
                          aria-pressed={draft.skin.structural[k.key] === o}
                          onClick={() => setStructural(k.key, o)}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                    <p className="note" style={{ marginTop: 3 }}>
                      {k.note}
                    </p>
                  </div>
                ))}
                <p className="note">
                  Bar heights, the dock, the bevel, the CRT field and the type stack stay with the
                  master. A fork that could reshape the shell would stop being a fork.
                </p>
              </div>

              <div className="sheet">
                <p className="section-h">
                  preview <span className="dim">the desktop is the preview</span>
                </p>
                <button
                  type="button"
                  className="switch"
                  aria-pressed={skinApplied}
                  onClick={() => setSkinApplied((v) => !v)}
                >
                  <span className="switch__track" aria-hidden="true">
                    <span className="switch__knob" />
                  </span>
                  <span>
                    {skinApplied
                      ? 'Applied — this window, the dock and the desktop are the preview'
                      : 'Apply this skin to the desktop'}
                  </span>
                </button>
                <p
                  style={{
                    fontFamily: 'var(--ds-font-mark)',
                    fontSize: 26,
                    letterSpacing: '0.02em',
                    color: 'var(--ui-text)',
                    margin: 0,
                  }}
                >
                  {draft.skin.wordmark}
                </p>
                <div className="hstack" style={{ marginTop: 'var(--s-3)' }}>
                  <button type="button" className="btn btn--primary">
                    Primary
                  </button>
                  <button type="button" className="btn">
                    Secondary
                  </button>
                  <span className="tag tag--ok">live</span>
                  <span className="tag tag--warn">fault</span>
                  <span className="lamp" aria-hidden="true" />
                </div>
                <input className="field" readOnly value={draft.skin.handle} style={{ marginTop: 'var(--s-3)' }} />
                <div className="prog-track" style={{ marginTop: 'var(--s-3)' }}>
                  <span className="prog-fill" style={{ width: '62%' }} />
                </div>
                <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
                  Body copy in the fork&rsquo;s own ink, with an{' '}
                  <span style={{ color: 'var(--ui-accent)' }}>accent affordance</span> and{' '}
                  <span style={{ color: 'var(--ui-info)' }}>metadata</span>.
                </p>
                <p className="note">
                  {skinApplied
                    ? 'The forged tokens are written on the document root by the same helper SKIN_BUILDER.EXE calls, so this is not a preview of the skin — it is the skin, applied. Switch it off, or close the desk, and the built-in skin returns.'
                    : 'The palette applies at runtime to the document root, which is why the preview is the desktop rather than a copy of it: a scoped copy of the palette would be a second place for the same value to live, and the token layer exists to stop that. Until it is applied, every control here is wearing the master.'}
                </p>
              </div>
            </>
          )}

          {current.id === 'surface' && (
            <div className="sheet">
              <p className="section-h">
                the surface <span className="dim">one mark · four lines</span>
              </p>
              <p className="cap">
                Everything else is what the Immersion is. This is what it says the moment it
                opens: the mark in the chrome, the line across the top, the notice that is still
                true today. A page that opens on nothing is a page nobody returns to.
              </p>
              <span className="lbl micro">The mark</span>
              <div className="hstack">
                {(['mark', 'custom', 'none'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className="btn"
                    aria-pressed={draft.surface.logoMode === m}
                    onClick={() => setDraft((d) => ({ ...d, surface: { ...d.surface, logoMode: m } }))}
                  >
                    {m === 'mark' ? 'brand mark' : m === 'custom' ? 'your own mark' : 'wordmark only'}
                  </button>
                ))}
              </div>
              <div className="row">
                <span className="row__k">Mark file</span>
                <span className="row__v mono">
                  {draft.surface.logoMode === 'custom'
                    ? draft.surface.logoName || 'no file chosen yet'
                    : draft.surface.logoMode === 'none'
                      ? 'none — the wordmark carries the corner'
                      : 'the brand mark, as shipped'}
                </span>
              </div>
              <div className="hstack">
                <label className="btn" htmlFor="wz-logo">
                  Choose file
                </label>
                <input
                  id="wz-logo"
                  type="file"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 307200) {
                      setMsg('That file is over 300 KB — resize it and choose it again.');
                      return;
                    }
                    const r = new FileReader();
                    r.onload = () =>
                      setDraft((d) => ({
                        ...d,
                        surface: {
                          ...d.surface,
                          logoData: String(r.result),
                          logoName: f.name,
                          logoMode: 'custom',
                        },
                      }));
                    r.readAsDataURL(f);
                  }}
                />
                {draft.surface.logoMode === 'custom' && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        surface: { ...d.surface, logoMode: 'mark', logoData: '', logoName: '' },
                      }))
                    }
                  >
                    Use the mark
                  </button>
                )}
              </div>
              {draft.surface.logoMode === 'custom' && draft.surface.logoData && (
                <div className="row">
                  <span className="row__k">Preview</span>
                  <span className="row__v">
                    <img
                      src={draft.surface.logoData}
                      alt="the operator's own mark"
                      width={32}
                      height={32}
                      style={{ imageRendering: 'pixelated' }}
                    />
                  </span>
                </div>
              )}
              <p className="note">
                The mark replaces the glyph in the chrome — menu bar, boot plate, window crests.
                It never replaces the wordmark, is never drawn into the wallpaper, and does not
                appear in the footer rule: the lockup is the brand, and a surface is a guest on it.
              </p>

              <p className="section-h">
                page load content <span className="dim">what a member reads first</span>
              </p>
              <p className="cap">
                The headline is the one line the Immersion says in its own voice; the notice is
                the only element anyone may change without a rebuild. Leave the notice empty and
                no strip is drawn at all.
              </p>
              <div className="grid2">
                <div>
                  <label className="lbl micro" htmlFor="wz-boot-headline">
                    Boot headline
                  </label>
                  <input
                    id="wz-boot-headline"
                    className="field"
                    placeholder="Welcome to the Immersion"
                    value={draft.surface.headline}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, surface: { ...d.surface, headline: e.target.value } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-boot-tagline">
                    Tagline
                  </label>
                  <input
                    id="wz-boot-tagline"
                    className="field"
                    placeholder="Four pillars, one container."
                    value={draft.surface.tagline}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, surface: { ...d.surface, tagline: e.target.value } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-boot-notice">
                    Notice bar
                  </label>
                  <input
                    id="wz-boot-notice"
                    className="field"
                    placeholder="Leave empty for no strip"
                    value={draft.surface.notice}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, surface: { ...d.surface, notice: e.target.value } }))
                    }
                  />
                </div>
                <div>
                  <label className="lbl micro" htmlFor="wz-boot-kind">
                    Notice severity
                  </label>
                  <select
                    id="wz-boot-kind"
                    className="field"
                    value={draft.surface.noticeKind}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        surface: {
                          ...d.surface,
                          noticeKind: e.target.value as SurfaceSettings['noticeKind'],
                        },
                      }))
                    }
                  >
                    <option value="info">Info — blue</option>
                    <option value="ok">Good — green</option>
                    <option value="warn">Attention — amber</option>
                    <option value="err">Blocked — red</option>
                  </select>
                </div>
              </div>
              <div>
                <span className="lbl micro">Wallpaper</span>
                <div className="hstack">
                  {(['dither', 'grid', 'flat'] as const).map((w) => (
                    <button
                      key={w}
                      type="button"
                      className="btn"
                      aria-pressed={draft.surface.wallpaper === w}
                      onClick={() =>
                        setDraft((d) => ({ ...d, surface: { ...d.surface, wallpaper: w } }))
                      }
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
              <p className="note">
                Dither, grid or flat — the ground is a ground. A photograph of a beach is not a
                wallpaper in an operating system. The notice above the desks is the one piece of
                chrome a desk may set for itself without a rebuild.
              </p>
            </div>
          )}

          {current.id === 'review' && (
            <>
              <div className="sheet">
                <p className="section-h">
                  the container <span className="dim">derived</span>
                </p>
                <div className="row">
                  <span className="row__k">Account</span>
                  <span className="row__v mono">{account ?? <span className="tag tag--warn">unknown</span>}</span>
                </div>
                <div className="row">
                  <span className="row__k">State</span>
                  <span className="row__v">{charterSummary(charter)}</span>
                </div>
                <div className="row">
                  <span className="row__k">Holder</span>
                  <span className="row__v mono">{charter.holderEns ?? <span className="tag tag--warn">none</span>}</span>
                </div>
                <div className="row">
                  <span className="row__k">Minted</span>
                  <span className="row__v">
                    <span className="tag tag--muted">no — {String(charter.minted)}</span>
                  </span>
                </div>
              </div>

              <div className="sheet">
                <p className="section-h">
                  the charter <span className="dim">seven rights, as they stand</span>
                </p>
                <div className="rows">
                  {charter.rights.map((r) => (
                    <div className="item" key={r.key}>
                      <span
                        className={
                          r.status === 'on-purchase'
                            ? 'tag tag--ok'
                            : r.status === 'proposed'
                              ? 'tag tag--warn'
                              : 'tag tag--err'
                        }
                      >
                        {r.status}
                      </span>
                      <span className="grow">
                        <span className="lrow__t" style={{ whiteSpace: 'normal' }}>
                          {r.label}
                        </span>
                        <span className="cap">{r.grants}</span>
                        {r.blockedBy && <span className="note">blocked by {r.blockedBy}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sheet">
                <p className="section-h">
                  the answer <span className="dim">what this draft would hand over</span>
                </p>
                <dl className="kv">
                  <dt>Name</dt>
                  <dd>{draft.label}</dd>
                  <dt>id</dt>
                  <dd className="mono">{draft.id}</dd>
                  <dt>Pillars</dt>
                  <dd className="mono">
                    {draft.pillars.length} of {PILLARS.length}
                  </dd>
                  <dt>Roles</dt>
                  <dd className="mono">
                    {draft.roles.length} of {ROLES.length}
                  </dd>
                  <dt>Capabilities</dt>
                  <dd className="mono">
                    {bitsSet} of {PERMISSION_KEYS.length}
                  </dd>
                  <dt>Skin overrides</dt>
                  <dd className="mono">{skinDiff.length}</dd>
                  <dt>Revenue terms</dt>
                  <dd className="mono">
                    {charter.revenue.terms.map((t) => `${t.label} ${t.bps / 100}%`).join(' · ')}
                  </dd>
                  <dt>Split created</dt>
                  <dd>
                    <span className="tag tag--muted">no — {String(charter.revenue.created)}</span>
                  </dd>
                </dl>
                <p className="note">
                  The revenue split names {charter.revenue.terms.length} recipients and exists
                  nowhere: SplitMain ({shorten(charter.revenue.splitMain)}) is deployed, a split is
                  not.
                </p>
              </div>
            </>
          )}

          {current.id === 'issue' && (
            <>
              <div className="sheet">
                <p className="section-h">
                  what it writes <span className="dim">css · json · manifest</span>
                </p>
                <p className="cap">
                  The CSS is what we commit for an authored skin. The JSON is what an agent or a
                  buyer carries. The manifest is the draft itself, so a wizard run is reviewable
                  afterwards without this window.
                </p>
                <label className="lbl micro" htmlFor="wz-css">
                  Skin CSS
                </label>
                <textarea id="wz-css" className="field" readOnly rows={8} spellCheck={false} value={skinCss} />
                <label className="lbl micro" htmlFor="wz-json">
                  Manifest
                </label>
                <textarea id="wz-json" className="field" readOnly rows={10} spellCheck={false} value={manifest} />
                <div className="hstack">
                  <button type="button" className="btn" onClick={() => copy(skinCss, 'CSS')}>
                    Copy CSS
                  </button>
                  <button type="button" className="btn" onClick={() => copy(manifest, 'Manifest')}>
                    Copy manifest
                  </button>
                  <button type="button" className="btn" onClick={() => copy(skinToJson(draft.skin), 'Skin JSON')}>
                    Copy skin JSON
                  </button>
                </div>
              </div>

              <div className="sheet">
                <p className="section-h">
                  reserve <span className="dim">an intent, on this device</span>
                </p>
                <div className="hstack">
                  <button type="button" className="btn btn--primary" onClick={reserve}>
                    Record intent
                  </button>
                  <span className="mono">
                    {reservations.length} recorded on this device
                  </span>
                </div>
                {reservations.length > 0 && (
                  <div className="rows" style={{ marginTop: 'var(--s-3)' }}>
                    {reservations.slice(0, 5).map((r) => (
                      <div className="item" key={r.at}>
                        <span className="mono">{r.at.slice(0, 16).replace('T', ' ')}</span>
                        <span className="grow">
                          <span className="lrow__t">{r.name}</span>
                          <span className="note">
                            {r.account ? shorten(r.account) : 'no account derived'} ·{' '}
                            {r.roles.length} roles · {r.skin}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="notice notice--brass">
                  <span className="tick" aria-hidden="true" />
                  <span>
                    <strong>Nothing is sent.</strong> There is no Immersion collection to mint
                    against, so a reservation that claimed otherwise would be a lie. It is stored
                    in this browser, and it is cleared with the draft.
                  </span>
                </div>
                <div className="hstack">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setState({ step: 0, draft: blankDraft() });
                      setReservations([]);
                      try {
                        localStorage.removeItem(DRAFT_KEY);
                        localStorage.removeItem(RESERVE_KEY);
                      } catch {
                        /* non-fatal */
                      }
                      setMsg('Draft and reservations cleared.');
                    }}
                  >
                    Clear draft and reservations
                  </button>
                </div>
              </div>
            </>
          )}

          {msg && <p className="note">{msg}</p>}
        </div>

        <aside className="wz-rail">
          <div className="sheet">
            <p className="section-h">the draft</p>
            <dl className="kv">
              <dt>Name</dt>
              <dd>{draft.label}</dd>
              <dt>id</dt>
              <dd className="mono">{draft.id}</dd>
              <dt>ENS</dt>
              <dd className="mono">{draft.handle || 'unknown'}</dd>
              <dt>Chain</dt>
              <dd className="mono">{draft.chainId}</dd>
              <dt>Pillars</dt>
              <dd className="mono">{draft.pillars.length}/4</dd>
              <dt>Roles</dt>
              <dd className="mono">{draft.roles.length}/15</dd>
              <dt>Bits</dt>
              <dd className="mono">{bitsSet}/30</dd>
              <dt>Overrides</dt>
              <dd className="mono">{skinDiff.length}</dd>
              <dt>DAO</dt>
              <dd className="mono">{draft.dao.name.trim() || 'not named'}</dd>
              <dt>Mark</dt>
              <dd className="mono">{draft.surface.logoMode}</dd>
            </dl>
            <p className="section-h" style={{ marginTop: 'var(--s-4)' }}>
              container
            </p>
            {account ? (
              <code className="code" style={{ display: 'block', wordBreak: 'break-all' }}>
                {account}
              </code>
            ) : (
              <p className="note">unknown — complete step 02 to derive it.</p>
            )}
            <p className="section-h" style={{ marginTop: 'var(--s-4)' }}>
              draft state
            </p>
            <div className="hstack">
              <span className="badge badge-claude">not minted</span>
              <span className="badge badge-admin">local only</span>
              {skinApplied && <span className="badge badge-live">skin applied</span>}
            </div>
          </div>
          <p className="note">← → walks the steps. The draft survives a closed window.</p>
        </aside>
      </div>

      <p className="page__foot">
        An Immersion is assembled in order: a name, then a container derived from a token, then
        the modules, the roster, the governance that binds them, the palette and the surface.
        The wizard exists because that order is real —
        and it says <em>unknown</em> the moment an answer is missing.
      </p>
    </div>
  );
}
